/**
 * Core-100 R4 — CORE-BC GAP-001/002/003: the typed explanation of a placement decision, for the surface where the
 * assignment is actually made (OptiPlace).
 *
 * The server builds it (`supabase/functions/_shared/assignmentExplanation.ts`) from values the ranking already
 * computed; this module is the browser half of the same contract: the shape, a reader that accepts nothing it does
 * not recognise, and the judgment-language guard. Parity of the shape and of the word list with the server module is
 * pinned by tests/unit/core100/assignmentExplanation.test.ts.
 */
import { FORBIDDEN_PHRASES } from './terminology';

export type AssignmentSignalKey = 'proficiency' | 'efficiency' | 'workload' | 'history' | 'learned_time' | 'start';

export interface AssignmentExplanationSignal {
  key: AssignmentSignalKey;
  label: string;
  value: string;
  source: string;
  sampleSize: number | null;
  sampleUnit: string | null;
  /** The signal's own confidence (0–1) where its authority computes one (CORE-E). Never a banded verdict. */
  confidence: number | null;
  note: string | null;
}

export interface AssignmentExplanation {
  version: 1;
  summary: string;
  signals: AssignmentExplanationSignal[];
  sampleSize: number | null;
  confidence: number | null;
}

/**
 * Words that pass judgment on a PERSON where the system describes a measurement (CORE-BC responsibility 7: "a person
 * may be slower than average without being worse, and the system must not use the second word for the first").
 * Superset of the typed library's FORBIDDEN_PHRASES. Same list as the server module.
 */
export const JUDGMENT_WORDS = [
  'best', 'worst', 'better', 'worse', 'good', 'bad', 'poor', 'weak', 'strong', 'slow', 'lazy', 'careless',
  'sloppy', 'incompetent', 'unreliable', 'underperform', 'excellent', 'superior', 'inferior', 'guaranteed',
  'recommended action', 'will improve', 'specializes', 'expert', 'novice',
] as const;

export function findJudgmentLanguage(text: string): string[] {
  const lower = String(text ?? '').toLowerCase();
  return JUDGMENT_WORDS.filter((w) => new RegExp(`(^|[^a-z])${w.replace(/ /g, '\\s+')}([^a-z]|$)`).test(lower));
}

/** True when every phrase the typed library forbids is also caught here (no weaker guard on the decision surface). */
export function judgmentGuardCoversLibrary(): boolean {
  return FORBIDDEN_PHRASES.every((p) => (JUDGMENT_WORDS as readonly string[]).includes(p));
}

const KEYS: readonly AssignmentSignalKey[] = ['proficiency', 'efficiency', 'workload', 'history', 'learned_time', 'start'];
const numOrNull = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const strOrNull = (v: unknown): string | null => (typeof v === 'string' ? v : null);

/** Read an explanation off a ranking payload; null for anything that is not one (an older server, a bad payload). */
export function readAssignmentExplanation(raw: unknown): AssignmentExplanation | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (r.version !== 1 || typeof r.summary !== 'string' || !Array.isArray(r.signals)) return null;
  const signals: AssignmentExplanationSignal[] = [];
  for (const s of r.signals as unknown[]) {
    if (!s || typeof s !== 'object') continue;
    const x = s as Record<string, unknown>;
    if (!KEYS.includes(x.key as AssignmentSignalKey) || typeof x.label !== 'string' || typeof x.value !== 'string') continue;
    signals.push({
      key: x.key as AssignmentSignalKey,
      label: x.label,
      value: x.value,
      source: strOrNull(x.source) ?? '',
      sampleSize: numOrNull(x.sampleSize),
      sampleUnit: strOrNull(x.sampleUnit),
      confidence: numOrNull(x.confidence),
      note: strOrNull(x.note),
    });
  }
  return { version: 1, summary: r.summary, signals, sampleSize: numOrNull(r.sampleSize), confidence: numOrNull(r.confidence) };
}

/** "Based on 12 pieces of evidence" / "No sample basis" — the data-point line for one signal. */
export function describeSignalSample(s: AssignmentExplanationSignal): string | null {
  if (s.sampleSize === null) return s.key === 'proficiency' || s.key === 'efficiency' ? 'No sample basis recorded' : null;
  return `Based on ${s.sampleSize} ${s.sampleUnit ?? 'observations'}`;
}

/** CORE-E confidence shown as the Passport shows it — a percentage, not a band. */
export function describeSignalConfidence(s: AssignmentExplanationSignal): string | null {
  return s.confidence === null ? null : `Confidence ${Math.round(s.confidence * 100)}%`;
}
